import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const toTimestamp = value => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value?.toDate) return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    // Handle ISO string format
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.getTime();
  }
  return null;
};

const normalizeStatus = data => {
  let status =
    data.status ||
    data.tripStatus ||
    data.state ||
    data.currentStatus ||
    null;

  if (!status) {
    if (data.departureDate) {
      const departureDate = data.departureDate?.toDate
        ? data.departureDate.toDate()
        : new Date(data.departureDate);
      const now = new Date();
      if (
        departureDate instanceof Date &&
        !Number.isNaN(departureDate.getTime())
      ) {
        if (departureDate < now && now - departureDate > 7 * 24 * 60 * 60 * 1000) {
          status = 'completed';
        } else {
          status = 'active';
        }
      }
    }
    if (!status) {
      status = 'active';
    }
  }

  return status?.toString().toLowerCase().trim() || 'active';
};

const ACTIVE_ALIAS_STATUSES = new Set([
  'active',
  'pending',
  'in-progress',
  'processing',
  'open',
  'listed',
  'looking',
  'available',
]);

const INACTIVE_STATUSES = new Set([
  'completed',
  'done',
  'closed',
  'delivered',
  'fulfilled',
  'cancelled',
  'canceled',
  'expired',
  'archived',
]);

const filterTripsByStatus = (trips, targetStatus) => {
  const normalized = targetStatus?.toString().toLowerCase().trim();

  if (!normalized || normalized === 'all') {
    return trips;
  }

  if (normalized === 'active') {
    return trips.filter(trip => !INACTIVE_STATUSES.has(trip.status));
  }

  return trips.filter(trip => trip.status === normalized);
};

export function useActiveTrips(status = 'active', options = {}) {
  const { enabled = true } = options;
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setTrips([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);

    const tripsRef = collection(db, 'trips');

    const unsubscribe = onSnapshot(
      tripsRef,
      snapshot => {
        const mappedTrips = snapshot.docs.map(doc => {
          const data = doc.data();
          const status = normalizeStatus(data);
          return {
            id: doc.id,
            ...data,
            status,
            createdAt: toTimestamp(data.createdAt),
            updatedAt: toTimestamp(data.updatedAt),
          };
        });

        const nextTrips = filterTripsByStatus(mappedTrips, status);

        nextTrips.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt - a.createdAt;
        });

        setTrips(nextTrips);
        setLoading(false);
        setError(null);
      },
      snapshotError => {
        console.error('Trips listener error', snapshotError);
        setError('Unable to load trips. Please try again later.');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [status, enabled]);

  const carryingTrips = useMemo(
    () => trips.filter(trip => trip.tripType === 'carrying'),
    [trips],
  );

  const shippingTrips = useMemo(
    () => trips.filter(trip => trip.tripType === 'shipping'),
    [trips],
  );

  return {
    trips,
    carryingTrips,
    shippingTrips,
    loading,
    error,
  };
}

